import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bot, Layers, LifeBuoy, Sparkles, Lock, Zap } from "lucide-react";

const bespoke = [
  {
    icon: Bot,
    title: "Custom AI & Automation Pipelines",
    desc: "Stop wasting human capital on repetitive processes. We audit your workflows and engineer autonomous AI agents and intelligent data pipelines to handle complex reasoning, content synthesis, and analytics at scale.",
    tags: ["LLM Orchestration", "Agentic Workflows", "RAG", "Eval Suites"],
  },
  {
    icon: Layers,
    title: "Full-Stack App & Web Engineering",
    desc: "From pixel-perfect, premium web platforms to deep, database-driven mobile and desktop applications. Beautifully designed, ruthlessly optimized for conversions, and built to handle enterprise-level traffic.",
    tags: ["React / Next", "FastAPI / Node", "Postgres / Mongo", "Edge Deploy"],
  },
  {
    icon: LifeBuoy,
    title: "Technical Product Rescue & Scoping",
    desc: "Have an unfinished product or an agency project that went off the rails? We step in, audit the codebase, restructure the project roadmap, and steer the ship to a successful, high-velocity launch.",
    tags: ["Codebase Audit", "Roadmap Reset", "Velocity Recovery", "Hand-off Ready"],
  },
];

const proprietary = [
  {
    icon: Sparkles,
    title: "Agent Orchestrator (Beta)",
    desc: "A multi-agent runtime for enterprise workflows — schedule, evaluate, and observe autonomous AI agents executing real business operations.",
    status: "Q3 Rollout",
  },
  {
    icon: Lock,
    title: "Vault Pipelines",
    desc: "Pre-built ingestion + governance layer for sensitive enterprise data. RBAC, audit logs, and provenance — out of the box.",
    status: "Private Preview",
  },
  {
    icon: Zap,
    title: "Velocity Studio",
    desc: "Project intelligence platform built for boutique studios. Sprint mapping, scope-creep alerts, and stakeholder telemetry in one IDE-grade dashboard.",
    status: "In Lab",
  },
];

export default function Solutions() {
  const [tab, setTab] = useState("bespoke");

  return (
    <div className="bg-[#0B0C10]">
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden" data-testid="solutions-hero">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">
            // SOLUTIONS &amp; PRODUCTS
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            Bespoke for clients. <br />
            <span className="text-[#FF3355]">Proprietary for the market.</span>
          </h1>
          <p className="mt-8 text-[#C5C6C7] max-w-2xl leading-relaxed">
            We split our energy between elite client engagements and a continuous laboratory of
            standalone AI platforms designed to disrupt outdated software models.
          </p>
        </div>
      </section>

      {/* Tab filter */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="inline-grid grid-cols-2 border border-[#1F2833] mb-12">
            <button
              data-testid="solutions-tab-bespoke"
              onClick={() => setTab("bespoke")}
              className={`px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-colors ${
                tab === "bespoke"
                  ? "bg-[#B91C2C] text-[#0B0C10]"
                  : "bg-transparent text-[#C5C6C7] hover:text-[#F5F5F7]"
              }`}
            >
              [ Bespoke Enterprise Solutions ]
            </button>
            <button
              data-testid="solutions-tab-proprietary"
              onClick={() => setTab("proprietary")}
              className={`px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-colors ${
                tab === "proprietary"
                  ? "bg-[#B91C2C] text-[#0B0C10]"
                  : "bg-transparent text-[#C5C6C7] hover:text-[#F5F5F7]"
              }`}
            >
              [ Proprietary AI Products ]
            </button>
          </div>

          {tab === "bespoke" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
              {bespoke.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.title}
                    data-testid={`solution-card-${s.title.split(" ")[0].toLowerCase()}`}
                    className="bg-[#0B0C10] p-8 te-glow-hover border border-transparent hover:border-[#B91C2C] flex flex-col h-full"
                  >
                    <Icon size={22} className="text-[#FF3355]" />
                    <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-4 text-sm text-[#C5C6C7] leading-relaxed flex-1">{s.desc}</p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {s.tags.map((t) => (
                        <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#B91C2C] border border-[#1F2833] px-2 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              {/* Spotlight */}
              <div className="border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12 te-glow-hover" data-testid="proprietary-spotlight">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// PRODUCT SPOTLIGHT</div>
                <h3 className="mt-4 font-display text-3xl lg:text-5xl font-bold tracking-tighter">
                  INTELLIGENCE IN <span className="text-[#FF3355]">DEPLOYMENT.</span>
                </h3>
                <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">
                  TraceElements isn&rsquo;t just a service provider; we are a continuous laboratory. We
                  continuously develop, test, and launch standalone SaaS tools and automated AI platforms
                  designed to disrupt outdated software models.
                </p>
                <div className="mt-8 font-mono text-xs text-[#C5C6C7]">
                  <span className="text-[#FF3355]">&gt;</span> Stay tuned for our upcoming Q3 product rollouts.
                </div>
              </div>

              {/* Product cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
                {proprietary.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.title} className="bg-[#0B0C10] p-8 te-glow-hover border border-transparent hover:border-[#B91C2C]" data-testid={`product-card-${p.title.split(" ")[0].toLowerCase()}`}>
                      <Icon size={20} className="text-[#FF3355]" />
                      <div className="mt-6 flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C] border border-[#B91C2C]/50 px-2 py-0.5">
                          {p.status}
                        </span>
                      </div>
                      <h4 className="mt-3 font-display text-xl font-bold tracking-tight">{p.title}</h4>
                      <p className="mt-3 text-sm text-[#C5C6C7] leading-relaxed">{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA strip */}
          <div className="mt-16 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-2">// NEXT.STEP</div>
              <h4 className="font-display text-2xl lg:text-3xl font-bold tracking-tight max-w-xl">
                Map your initiative onto a TraceElements track.
              </h4>
            </div>
            <Link
              to="/terminal"
              data-testid="solutions-cta-initialize"
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors w-fit"
            >
              Initialize Project <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
